import {
	InventoryCell,
	InventoryType as InventoryCellType,
	InventoryType,
	Item,
	ItemAction,
	ItemType,
	PackedInventory,
} from '~/shared';

import { rage, useObservable } from '~/rage';
import { Subject } from '~/rage/utils';

export interface BackpackState {
	id: string;
	cells: InventoryCell[];
}

export const useInventoryActions = () => {
	const inventoryIds = useObservable(rage.inventory.getInventoriesId());
	const secondaryInventoryId = useObservable(
		rage.inventory.getSecondaryInventoryId(),
	);

	const characterInventory = useObservable(
		inventoryIds[InventoryType.EQUIPMENT]
			? rage.inventory.getInventory(inventoryIds[InventoryType.EQUIPMENT])
			: new Subject<PackedInventory | null>(null),
	);

	const isClothingItem = (item: Item): boolean => {
		const clothesType = item.data?.clothes?.type;
		return item.type === ItemType.CLOTHES || Boolean(clothesType);
	};

	const isItemEquipped = (item: Item): boolean => {
		if (!characterInventory) return false;
		return characterInventory.items.some(
			(stack): boolean => !!stack && stack.id === item.id,
		);
	};

	const getAvailableActions = (
		item: Item,
		cellType: InventoryCellType,
		activeHand?: boolean,
	): ItemAction[] => {
		const baseActions = [...item.availableActions];

		if (cellType === InventoryCellType.DROPPED) {
			return baseActions.filter(
				(action) =>
					action !== ItemAction.SPLIT &&
					action !== ItemAction.DROP &&
					action !== ItemAction.USE,
			);
		}

		if (
			cellType === InventoryCellType.PLAYER ||
			cellType === InventoryCellType.BACKPACK ||
			cellType === InventoryCellType.VEHICLE ||
			cellType === InventoryCellType.HOUSE ||
			cellType === InventoryCellType.EQUIPMENT ||
			cellType === InventoryCellType.HOTBAR
		) {
			if (isClothingItem(item)) {
				const isEquipped = isItemEquipped(item);
				const equipIndex = baseActions.indexOf(ItemAction.EQUIP);
				const unequipIndex = baseActions.indexOf(ItemAction.UNEQUIP);

				if (isEquipped) {
					if (equipIndex !== -1) {
						baseActions[equipIndex] = ItemAction.UNEQUIP;
					} else if (unequipIndex === -1) {
						baseActions.push(ItemAction.UNEQUIP);
					}
				} else {
					if (unequipIndex !== -1) {
						baseActions[unequipIndex] = ItemAction.EQUIP;
					} else if (equipIndex === -1) {
						baseActions.push(ItemAction.EQUIP);
					}
				}
			} else {
				const equipIndex = baseActions.indexOf(ItemAction.EQUIP);
				const unequipIndex = baseActions.indexOf(ItemAction.UNEQUIP);

				if (equipIndex !== -1) baseActions.splice(equipIndex, 1);
				if (unequipIndex !== -1 && !activeHand) {
					baseActions.splice(unequipIndex, 1);
				}
			}

			if (item.count > 1) {
				if (!baseActions.includes(ItemAction.SPLIT)) {
					baseActions.push(ItemAction.SPLIT);
				}
			} else {
				const splitIndex = baseActions.indexOf(ItemAction.SPLIT);
				if (splitIndex !== -1) {
					baseActions.splice(splitIndex, 1);
				}
			}

			const isGiveHandsAvailable = item.availableActions.includes(
				ItemAction.GIVE_HANDS,
			);

			if (isGiveHandsAvailable || activeHand) {
				const giveHandsIndex = baseActions.indexOf(ItemAction.GIVE_HANDS);
				const takeHandsIndex = baseActions.indexOf(ItemAction.TAKE_HANDS);

				const findInsertPosition = (): number => {
					const useIndex = baseActions.indexOf(ItemAction.USE);
					const splitIndex = baseActions.indexOf(ItemAction.SPLIT);
					const dropIndex = baseActions.indexOf(ItemAction.DROP);

					if (splitIndex !== -1) return splitIndex;
					if (dropIndex !== -1) return dropIndex;
					if (useIndex !== -1) return useIndex + 1;
					return baseActions.length;
				};

				if (activeHand) {
					if (giveHandsIndex !== -1) {
						baseActions.splice(giveHandsIndex, 1);
					}
					if (takeHandsIndex !== -1) {
						baseActions.splice(takeHandsIndex, 1);
					}
					const insertIndex = findInsertPosition();
					baseActions.splice(insertIndex, 0, ItemAction.TAKE_HANDS);
				} else if (isGiveHandsAvailable) {
					if (takeHandsIndex !== -1) {
						baseActions.splice(takeHandsIndex, 1);
					}
					if (giveHandsIndex !== -1) {
						baseActions.splice(giveHandsIndex, 1);
					}
					const insertIndex = findInsertPosition();
					baseActions.splice(insertIndex, 0, ItemAction.GIVE_HANDS);
				}
			}
		}

		return baseActions;
	};

	const callMakeAction = (
		inventoryId: string | undefined,
		item: Item,
		action: ItemAction,
		itemSlot: number,
	) => {
		if (!inventoryId) return;
		rage.inventory
			.performItemAction(
				inventoryId,
				item.id,
				item.count,
				item.data,
				itemSlot,
				action,
			)
			.catch(() => undefined);
	};

	const handleInventoryAction = (
		action: ItemAction,
		item: Item,
		sourceType: InventoryCellType,
		itemSlot: number,
	) => {
		const typeToId: Record<InventoryCellType, string | undefined> = {
			[InventoryCellType.PLAYER]: inventoryIds[InventoryType.PLAYER],
			[InventoryCellType.EQUIPMENT]: inventoryIds[InventoryType.EQUIPMENT],
			[InventoryCellType.HOTBAR]: inventoryIds[InventoryType.HOTBAR],
			[InventoryCellType.BACKPACK]: inventoryIds[InventoryType.BACKPACK],
			[InventoryCellType.VEHICLE]: secondaryInventoryId ?? undefined,
			[InventoryCellType.HOUSE]: secondaryInventoryId ?? undefined,
			[InventoryCellType.EXCHANGE]: undefined,
			[InventoryCellType.DROPPED]: secondaryInventoryId ?? undefined,
		};
		callMakeAction(typeToId[sourceType], item, action, itemSlot);
	};

	const handleCharacterAction = (
		action: ItemAction,
		item: Item,
		itemSlot: number,
	) => {
		const inventoryId = inventoryIds[InventoryType.EQUIPMENT];
		callMakeAction(inventoryId, item, action, itemSlot);
	};

	return {
		handleInventoryAction,
		handleCharacterAction,
		getAvailableActions,
	};
};
